/**
 * AgencyAnalytics' "Visualization" tiles: switch a data widget between Stat,
 * Column, Line, Area, Horizontal, Pie, Donut, Gauge, Sparkline, Stacked,
 * Table and Goal without rebuilding it. Switching to a type that needs a
 * dimension (pie, donut, horizontal, stacked) picks the source's first one.
 */

import type { ReactNode } from 'react';

import type { CatalogueSource, Widget, WidgetType } from './reports-api.ts';

export const VISUALS: { type: WidgetType; label: string }[] = [
  { type: 'METRIC', label: 'Stat' },
  { type: 'BAR', label: 'Column' },
  { type: 'LINE', label: 'Line' },
  { type: 'AREA', label: 'Area' },
  { type: 'HORIZONTAL_BAR', label: 'Horizontal' },
  { type: 'PIE', label: 'Pie' },
  { type: 'DONUT', label: 'Donut' },
  { type: 'GAUGE', label: 'Gauge' },
  { type: 'SPARKLINE', label: 'Sparkline' },
  { type: 'STACKED_BAR', label: 'Stacked' },
  { type: 'TABLE', label: 'Table' },
  { type: 'GOAL', label: 'Goal' },
];

export const NEEDS_DIMENSION: WidgetType[] = ['DONUT', 'PIE', 'HORIZONTAL_BAR', 'STACKED_BAR'];
export const ONE_METRIC: WidgetType[] = ['METRIC', 'GOAL', 'GAUGE', 'SPARKLINE', 'DONUT', 'PIE'];

export function VisualPicker({
  widget,
  source,
  onChange,
}: {
  widget: Widget;
  source: CatalogueSource | undefined;
  onChange: (widget: Widget) => void;
}): ReactNode {
  const pick = (type: WidgetType) => {
    const data = { ...widget.data };
    if (NEEDS_DIMENSION.includes(type) && !data.dimension) {
      data.dimension =
        source?.dimensions.find((dimension) => dimension.key !== 'date')?.key ?? null;
    }
    if (ONE_METRIC.includes(type)) data.metrics = (data.metrics ?? []).slice(0, 1);
    onChange({ ...widget, type, data });
  };
  return (
    <fieldset className="rs-visuals">
      <legend>Visualization</legend>
      <div className="rs-visual-grid">
        {VISUALS.map((visual) => (
          <button
            type="button"
            key={visual.type}
            className={`rs-visual${widget.type === visual.type ? ' on' : ''}`}
            aria-pressed={widget.type === visual.type}
            onClick={() => pick(visual.type)}
          >
            <span
              className={`rs-visual-icon rs-visual-${visual.type.toLowerCase()}`}
              aria-hidden="true"
            />
            {visual.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
